import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { RootState, ThunkApi } from 'state/store';
import { Status } from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';
import { UserSummary } from 'types/store/normalized';

/* ============================== STATE SETUP =============================== */
interface UserSummariesState {
  status: Status;
  cursor?: string;
  hasMore?: boolean;
  loadingUserIds: string[];
  notFoundUserIds: string[];
}

interface GetListParams {
  userIds?: string[];
  pageSize?: number;
  userType?: string;
  userName?: string;
}

interface GetUserSummariesResponse {
  data: UserSummary[];
  totalCount: number;
  hasMore?: boolean;
  cursor: string;
}

const initialState: UserSummariesState = {
  status: SLICE_STATUS.IDLE,
  cursor: '',
  hasMore: false,
  loadingUserIds: [],
  notFoundUserIds: [],
};

const adapter = createEntityAdapter<UserSummary>();

const selectUserSummariesState = (state: RootState) =>
  state.normalized.userSummaries;

const NO_OP_RESPONSE: GetUserSummariesResponse = {
  data: [],
  totalCount: 0,
  cursor: '',
  hasMore: false,
};

/* ============================== HELPERS =================================== */
function getNewUserIdsToFetch(state: RootState, userIds: string[]): string[] {
  const { entities, notFoundUserIds = [] } = selectUserSummariesState(state);

  const notFoundSet = new Set(notFoundUserIds);

  return userIds.filter((id) => {
    const isCached = Boolean(entities[id]);
    const isNotFound = notFoundSet.has(id);
    return !isNotFound && !isCached;
  });
}

/* ============================== REDUX THUNK =============================== */
function concurrencyCondition(
  { userIds = [] }: GetListParams,
  { getState }: { getState: () => RootState }
): boolean {
  const state = getState();
  const { status } = selectUserSummariesState(state);

  if (userIds.length === 0) return status !== SLICE_STATUS.LOADING;

  return true;
}

export const getUserSummaries = createAsyncThunk<
  GetUserSummariesResponse,
  GetListParams,
  ThunkApi
>(
  'normalized/userSummary/getUserSummaries',
  async (
    { pageSize, userIds, userName },
    { getState, extra: { getUserSummariesApi } }
  ) => {
    const state = getState();
    const { hasMore, cursor } = selectUserSummariesState(state);

    const initialUserIds = (userIds ?? []).filter((id) => id != null);
    let idsToFetch = initialUserIds;

    if (initialUserIds.length > 0) {
      idsToFetch = getNewUserIdsToFetch(state, initialUserIds);

      if (idsToFetch.length === 0) {
        return NO_OP_RESPONSE;
      }
    }

    const baseParams = {
      ...(idsToFetch.length > 0 ? { userIds: idsToFetch } : {}),
      pageSize,
      userName,
    };

    const queryParams =
      hasMore && cursor ? { ...baseParams, after: cursor } : baseParams;

    const { data, meta } = await getUserSummariesApi().getByIdsPost({
      ...queryParams,
    });

    return {
      data,
      totalCount: meta.totalCount,
      cursor: meta.cursor,
      hasMore: meta.hasMore,
    };
  },
  { condition: concurrencyCondition }
);

/* ================================= REDUCER ================================ */
const userSummariesSlice = createSlice({
  name: 'normalized/userSummary',
  initialState: adapter.getInitialState(initialState),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getUserSummaries.pending, (state, action) => {
        const { userIds = [] } = action.meta.arg || {};
        state.status = SLICE_STATUS.LOADING;

        if (userIds.length > 0) {
          const current = new Set(state.loadingUserIds);
          userIds.forEach((id) => current.add(id));
          state.loadingUserIds = Array.from(current);
        }
      })
      .addCase(getUserSummaries.rejected, (state, action) => {
        const { userIds = [] } = action.meta.arg || {};

        state.status = SLICE_STATUS.FAILED;

        state.loadingUserIds = state.loadingUserIds.filter(
          (id) => !userIds.includes(id)
        );

        state.cursor = '';
        state.hasMore = false;
      })
      .addCase(getUserSummaries.fulfilled, (state, action) => {
        const { userIds = [] } = action.meta.arg || {};

        state.loadingUserIds = state.loadingUserIds.filter(
          (id) => !userIds.includes(id)
        );

        adapter.upsertMany(state, action.payload.data);
        state.cursor = action.payload.cursor;
        state.hasMore = action.payload.hasMore;
        state.status = SLICE_STATUS.SUCCESS;

        const returnedIds = action.payload.data.map((u) => u.id);
        const requestedNotFound = userIds.filter(
          (id) => !returnedIds.includes(id)
        );

        if (requestedNotFound.length > 0) {
          const current = new Set(state.notFoundUserIds);
          requestedNotFound.forEach((id) => current.add(id));
          state.notFoundUserIds = Array.from(current);
        }
      });
  },
});

/* =============================== SELECTORS ================================ */
const defaultSelectors = adapter.getSelectors<RootState>(
  (state) => state.normalized.userSummaries
);

const selectHasMore = (state: RootState) =>
  state.normalized.userSummaries.hasMore;

const selectStatus = (state: RootState) =>
  state.normalized.userSummaries.status;

/**
 * Returns a new array per call, so callers MUST pass `shallowEqual` as
 * useSelector's second argument. See the selector invariant in
 * `state/normalized/reducers.ts`.
 */
const selectUserSummariesByIds = (state: RootState, ids: string[]) =>
  ids
    .map((id) => defaultSelectors.selectById(state, id))
    .filter((summary): summary is UserSummary => summary !== undefined);

export const userSummarySelectors = {
  ...defaultSelectors,
  selectStatus,
  selectUserSummariesByIds,
  selectHasMore,
};

export default userSummariesSlice.reducer;
